import {Component, computed, DestroyRef, inject, OnInit, signal, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {ColComponent, ContainerComponent, RowComponent} from "@coreui/angular";
import {MatFormField, MatHint, MatLabel} from "@angular/material/form-field";
import {MatOption, MatSelect} from "@angular/material/select";
import {MatButton} from "@angular/material/button";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatDialog} from "@angular/material/dialog";
import {MatTabsModule} from "@angular/material/tabs";
import {MatIcon} from "@angular/material/icon";
import {MatCardModule} from "@angular/material/card";
import {GeographyService} from "../../services/geography.service";
import {ErrorHandlingService} from "../../services/error-handling.service";
import {ICountry} from "../../interfaces/ICountry";
import {IRegion} from "../../interfaces/IRegion";
import {IRegionTaxRate} from "../../interfaces/IRegionTaxRate";
import {RegionTypes} from "../../enum/RegionTypes";
import {RegionTaxRateHistoryComponent} from "./region-tax-rate-history/region-tax-rate-history.component";
import {EditRegionTaxRateDialogComponent} from "../../components/dialogs/edit-region-tax-rate/edit-region-tax-rate-dialog.component";

@Component({
  selector: 'app-manage-region-tax-rates',
  standalone: true,
  imports: [
    ContainerComponent,
    RowComponent,
    ColComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatHint,
    MatSelect,
    MatOption,
    MatButton,
    MatProgressSpinner,
    MatTabsModule,
    MatIcon,
    MatCardModule,
    RegionTaxRateHistoryComponent
  ],
  templateUrl: './manage-region-tax-rates.component.html',
  styleUrl: './manage-region-tax-rates.component.css'
})
export class ManageRegionTaxRatesComponent implements OnInit {

  @ViewChild(RegionTaxRateHistoryComponent) public taxRateHistoryComponent: RegionTaxRateHistoryComponent | undefined;

  private geographyService = inject(GeographyService);
  private destroyRef = inject(DestroyRef);
  private errorHandlingService = inject(ErrorHandlingService);
  private matDialog = inject(MatDialog);

  public countries: ICountry[] = [];
  public regions: IRegion[] = [];
  public countriesLoaded = signal(false);
  public taxRatesLoaded = signal(false);
  public selectedRegion = signal<IRegion | null>(null);
  public currentTaxRate = signal<IRegionTaxRate | null>(null);

  public selectedRegionId = computed(() => this.selectedRegion()?.Id ?? null);
  public selectedRegionType = computed(() => this.selectedRegion()?.RegionType ?? RegionTypes.Unknown);

  public selectionForm = new FormGroup({
    country: new FormControl<string>('', [Validators.required]),
    region: new FormControl<string>('', [Validators.required])
  });

  public get pstLabel(): string {
    // Returns just the tax name; the template appends " Rate" (matching the GST/HST labels and the
    // edit dialog's pstLabel). Returning "PST Rate"/"QST Rate" here rendered "PST Rate Rate".
    return this.selectedRegionType() === RegionTypes.Quebec ? 'QST' : 'PST';
  }

  public formatRate(decimalRate: number): string {
    const pct = Math.round(decimalRate * 100000) / 1000;
    return pct === 0 ? '0%' : `${pct}%`;
  }

  public get hasScheduledRate(): boolean {
    return this.taxRateHistoryComponent?.hasScheduledRate ?? false;
  }

  ngOnInit(): void {
    this.loadCountries();
  }

  private loadCountries(): void {
    this.geographyService.allCountriesWithRegions().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (countries) => {
        this.countries = countries;
        this.countriesLoaded.set(true);
      },
      error: (err) => {
        this.countriesLoaded.set(true);
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  onCountryChange(): void {
    const countryId = this.selectionForm.controls.country.value;
    if (countryId) {
      const selectedCountry = this.countries.find(c => c.Id === countryId);
      this.regions = selectedCountry?.Regions || [];
      this.selectionForm.controls.region.reset();
      this.selectedRegion.set(null);
      this.currentTaxRate.set(null);
      this.taxRatesLoaded.set(false);
    }
  }

  onRegionChange(): void {
    const regionId = this.selectionForm.controls.region.value;
    if (regionId) {
      const region = this.regions.find(r => r.Id === regionId) ?? null;
      this.selectedRegion.set(region);
      this.loadTaxRates(regionId);
    }
  }

  private loadTaxRates(regionId: string): void {
    this.taxRatesLoaded.set(false);
    this.geographyService.getActiveTaxRateForRegion(regionId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (taxRate: IRegionTaxRate) => {
        this.currentTaxRate.set(taxRate);
        this.taxRatesLoaded.set(true);
      },
      error: (err) => {
        this.taxRatesLoaded.set(true);
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  openEditDialog(): void {
    const regionId = this.selectedRegionId();
    const currentRate = this.currentTaxRate();
    if (!regionId || !currentRate) return;

    const dialogRef = this.matDialog.open(EditRegionTaxRateDialogComponent, {
      data: {
        currentRate,
        regionId,
        regionType: this.selectedRegionType()
      },
      width: '500px'
    });

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(updateSuccessful => {
      if (updateSuccessful) {
        this.loadTaxRates(regionId);
        this.taxRateHistoryComponent?.reload();
      }
    });
  }
}
